import { create } from "zustand";
import type { MyProject, MyTeam } from "@domain/schemas/workspace";
import { useRecentlyVisitedStore } from "@presentation/stores/RecentlyVisitedStore";
import { useWorkspaceFilterStore } from "@presentation/stores/WorkspaceFilterStore";

const WORKSPACE_CONTEXT_STORAGE_KEY = "activeTeamContext";
const TEAM_ID_URL_PARAM = "teamId";
const PROJECT_ID_URL_PARAM = "projectId";

/**
Distinguishes "the API said you have no teams" from "the API has not answered yet".
*/
export type WorkspaceHydrationStatus = "idle" | "loading" | "loaded" | "error";

type PersistedWorkspaceContext = {
  activeTeamId: string;
  activeProjectId: string;
};

type WorkspaceContextStore = PersistedWorkspaceContext & {
  teams: MyTeam[];
  projects: MyProject[];
  status: WorkspaceHydrationStatus;
  error: unknown;

  setActiveTeamContext: (teamId: string, projectId?: string) => void;
  /**
  Selecting a team drops an active project that does not belong to it.
  */
  setActiveTeamId: (teamId: string) => void;
  /**
  Selecting a project moves the active team to that project's owning team.
  */
  setActiveProjectId: (projectId: string) => void;

  setStatus: (status: WorkspaceHydrationStatus) => void;
  setError: (error: unknown) => void;
  /**
  Installs API-sourced teams and projects, then reconciles the persisted context against them.
  */
  setWorkspace: (teams: MyTeam[], projects: MyProject[]) => void;

  /**
   * A request scoped to the active team came back denied or not-found (WP6.5) - the team was
   * deleted, or the user's access to it was revoked, after the workspace was last loaded. A no-op
   * if `teamId` is not the currently active one, so a response for a since-abandoned request
   * cannot discard a context the user has since switched to. Drops the team (and its projects)
   * from the known lists and reconciles, the same fallback already used at hydration time -
   * never a raw error, never an infinite retry against an id that will never resolve again.
   */
  recoverFromStaleTeam: (teamId: string) => void;
  /**
  Project-scope counterpart of {@link recoverFromStaleTeam}; only drops the one project.
  */
  recoverFromStaleProject: (projectId: string) => void;

  clear: () => void;
};

const emptyContext: PersistedWorkspaceContext = { activeTeamId: "", activeProjectId: "" };

const readPersistedWorkspaceContext = (): PersistedWorkspaceContext => {
  try {
    const rawValue = localStorage.getItem(WORKSPACE_CONTEXT_STORAGE_KEY);
    if (!rawValue) {
      return { ...emptyContext };
    }

    const parsedValue = JSON.parse(rawValue) as Partial<PersistedWorkspaceContext>;
    return {
      activeTeamId: parsedValue.activeTeamId ?? "",
      activeProjectId: parsedValue.activeProjectId ?? "",
    };
  } catch {
    return { ...emptyContext };
  }
};

const persistWorkspaceContext = (value: PersistedWorkspaceContext) => {
  try {
    localStorage.setItem(WORKSPACE_CONTEXT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Losing persistence costs the user their context on the next reload, which
    // the reconciler recovers from. It must not fail the interaction.
  }
};

/**
 * Reads a `teamId`/`projectId` deep-link pair from the current URL, if either is present.
 *
 * Returns `null` rather than an empty-string pair when neither param is present, so a plain
 * reload without query params falls through to the persisted (localStorage) context instead of
 * clobbering it with blanks.
 */
const readUrlWorkspaceContext = (): PersistedWorkspaceContext | null => {
  const params = new URLSearchParams(globalThis.location?.search);
  const teamId = params.get(TEAM_ID_URL_PARAM);
  const projectId = params.get(PROJECT_ID_URL_PARAM);
  return !teamId && !projectId ? null : { activeTeamId: teamId ?? "", activeProjectId: projectId ?? "" };
};

/**
 * Reflects the active context in the URL (`replaceState`, not `pushState`, so switching context
 * does not spam the browser history) so a deep link resolves the intended team/project per
 * `FR-UAUTH-TEAMPRJ-006`. Params are omitted entirely rather than written empty, keeping a
 * context-less URL clean.
 */
const writeUrlWorkspaceContext = (value: PersistedWorkspaceContext) => {
  try {
    const url = new URL(location.href);
    // Settings owns its query-string scope; background hydration must not replace a bookmarked scope.
    if (["/", "/projects", "/jobs", "/job-setup", "/teams", "/settings", "/about"].includes(url.pathname)) return;
    // The /teams/:teamId(/projects/:projectId)? routes already encode the active context in the
    // path itself - mirroring it into query params there would just duplicate it in the URL.
    const isContextEncodedInPath = url.pathname.startsWith("/teams/");
    if (!isContextEncodedInPath && value.activeTeamId) url.searchParams.set(TEAM_ID_URL_PARAM, value.activeTeamId);
    else url.searchParams.delete(TEAM_ID_URL_PARAM);
    if (!isContextEncodedInPath && value.activeProjectId) url.searchParams.set(PROJECT_ID_URL_PARAM, value.activeProjectId);
    else url.searchParams.delete(PROJECT_ID_URL_PARAM);
    history.replaceState(history.state, "", url);
  } catch {
    // Same reasoning as persistWorkspaceContext: a deep link that fails to update must not fail the interaction.
  }
};

/**
Writes the active context to both localStorage and the URL, kept in lockstep everywhere the context changes.
*/
const syncWorkspaceContext = (value: PersistedWorkspaceContext) => {
  persistWorkspaceContext(value);
  writeUrlWorkspaceContext(value);
};

/**
 * Picks the team to fall back to when the persisted one is gone.
 *
 * There is no "default team" marker on `MyTeam`, so the first entry is used.
 * `GET /teams` has no documented ordering, which makes this arbitrary but
 * stable enough: the user is shown which team is active and can change it.
 *
 * Falls back to the owning team of the first project when the user has no team
 * memberships at all. That is a legitimate state, not an edge case - a user can
 * hold a project role in a team they are not a member of, and `GET /teams` will
 * not list it - and picking an empty team here would then strand them with an
 * apparently empty workspace despite having accessible projects.
 */
const resolveFallbackTeamId = (teams: MyTeam[], projects: MyProject[]): string => teams[0]?.id ?? projects[0]?.teamId ?? "";

/**
Picks the project to fall back to within a team, preferring the user's own projects.
*/
const resolveFallbackProjectId = (projects: MyProject[], teamId: string): string => projects.find((project) => project.teamId === teamId)?.id ?? "";

/**
 * Reconciles a persisted context against what the API actually returned.
 *
 * Tasks this covers:
 * - a persisted team that no longer resolves is discarded and replaced;
 * - a persisted project that no longer resolves is discarded *without* also
 *   discarding a still-valid team;
 * - a persisted project belonging to a different team than the active one is
 *   treated as stale, because the two ids must always agree.
 */
const reconcileContext = (persisted: PersistedWorkspaceContext, teams: MyTeam[], projects: MyProject[]): PersistedWorkspaceContext => {
  if (teams.length === 0 && projects.length === 0) {
    return { ...emptyContext };
  }

  const hasPersistedTeam = teams.some((team) => team.id === persisted.activeTeamId);
  const persistedProject = projects.find((project) => project.id === persisted.activeProjectId);

  // A valid persisted project can rescue a team id that is missing from
  // GET /teams - which happens legitimately, since a user can hold a project
  // role in a team they are not a member of.
  const activeTeamId = hasPersistedTeam ? persisted.activeTeamId : (persistedProject?.teamId ?? resolveFallbackTeamId(teams, projects));

  const isProjectInActiveTeam = persistedProject !== undefined && persistedProject.teamId === activeTeamId;
  const activeProjectId = isProjectInActiveTeam ? persisted.activeProjectId : resolveFallbackProjectId(projects, activeTeamId);

  return { activeTeamId, activeProjectId };
};

const persistedWorkspaceContext = readUrlWorkspaceContext() ?? readPersistedWorkspaceContext();

export const useWorkspaceContextStore = create<WorkspaceContextStore>((set) => ({
  ...persistedWorkspaceContext,
  teams: [],
  projects: [],
  status: "idle",
  error: undefined,

  setActiveTeamContext: (teamId, projectId = "") => {
    const nextState = { activeTeamId: teamId, activeProjectId: projectId };
    syncWorkspaceContext(nextState);
    set(nextState);
  },

  setActiveTeamId: (teamId) =>
    set((state) => {
      const activeProject = state.projects.find((project) => project.id === state.activeProjectId);
      const isKeepsProject = activeProject !== undefined && activeProject.teamId === teamId;
      const nextState = {
        activeTeamId: teamId,
        activeProjectId: isKeepsProject ? state.activeProjectId : resolveFallbackProjectId(state.projects, teamId),
      };

      if (state.activeTeamId === nextState.activeTeamId && state.activeProjectId === nextState.activeProjectId) {
        return state;
      }

      syncWorkspaceContext(nextState);
      return nextState;
    }),

  setActiveProjectId: (projectId) =>
    set((state) => {
      const project = state.projects.find((candidate) => candidate.id === projectId);
      const nextState = {
        activeTeamId: project?.teamId ?? state.activeTeamId,
        activeProjectId: projectId,
      };

      if (state.activeTeamId === nextState.activeTeamId && state.activeProjectId === nextState.activeProjectId) {
        return state;
      }

      syncWorkspaceContext(nextState);
      return nextState;
    }),

  setStatus: (status) => set({ status }),
  setError: (error) => set({ error }),

  setWorkspace: (teams, projects) =>
    set((state) => {
      const reconciled = reconcileContext({ activeTeamId: state.activeTeamId, activeProjectId: state.activeProjectId }, teams, projects);

      if (state.activeTeamId !== reconciled.activeTeamId || state.activeProjectId !== reconciled.activeProjectId) {
        syncWorkspaceContext(reconciled);
      }

      // Catches a project deleted by someone else - this browser's own "recent projects" otherwise
      // has no way to learn about it until it tries (and fails) to navigate there.
      useRecentlyVisitedStore.getState().reconcileWithKnownProjects(projects.map((project) => project.id));

      return { teams, projects, status: "loaded", error: undefined, ...reconciled };
    }),

  recoverFromStaleTeam: (teamId) =>
    set((state) => {
      if (state.activeTeamId !== teamId) return state;

      const teams = state.teams.filter((team) => team.id !== teamId);
      const projects = state.projects.filter((project) => project.teamId !== teamId);
      const reconciled = reconcileContext(emptyContext, teams, projects);
      syncWorkspaceContext(reconciled);
      return { teams, projects, ...reconciled };
    }),

  recoverFromStaleProject: (projectId) =>
    set((state) => {
      if (state.activeProjectId !== projectId) return state;

      const projects = state.projects.filter((project) => project.id !== projectId);
      const reconciled = reconcileContext({ activeTeamId: state.activeTeamId, activeProjectId: "" }, state.teams, projects);
      syncWorkspaceContext(reconciled);
      return { projects, ...reconciled };
    }),

  clear: () => {
    useWorkspaceFilterStore.getState().clear();
    syncWorkspaceContext(emptyContext);
    set({ ...emptyContext, teams: [], projects: [], status: "idle", error: undefined });
  },
}));

/**
True once the workspace has loaded and the user holds neither a team nor a project.
*/
// eslint-disable-next-line unicorn/consistent-boolean-name -- repo convention: every store selector is prefixed `select`, boolean or not
export const selectIsWorkspaceEmpty = (state: WorkspaceContextStore): boolean => state.status === "loaded" && state.teams.length === 0 && state.projects.length === 0;

export const selectActiveTeam = (state: WorkspaceContextStore): MyTeam | undefined => state.teams.find((team) => team.id === state.activeTeamId);

export const selectActiveProject = (state: WorkspaceContextStore): MyProject | undefined => state.projects.find((project) => project.id === state.activeProjectId);

export const selectProjectsForActiveTeam = (state: WorkspaceContextStore): MyProject[] => state.projects.filter((project) => project.teamId === state.activeTeamId);

export const WORKSPACE_CONTEXT_STORAGE_KEY_FOR_TESTS = WORKSPACE_CONTEXT_STORAGE_KEY;
export type { WorkspaceContextStore, PersistedWorkspaceContext };
